import { FormFieldsProps } from "@/components/form/Form";
import { fetchGenerateMdx } from "@/lib/ai/ai-server";
import { useCourse, useSection } from "@/lib/store";

import { CommonAdminFields, MarkdownField } from "@/lms/core/LmsFields";
import { Button } from "@/components/ui/button";
import { useRef, useState } from "react";
import CodeHike from "@/components/codehike/CodeHike";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

const Name = "Generate Markdown";

const MAX_INPUT_LENGTH = 400;

const Defaults = {
  code: "",
  type: "markdown",
};

function getIndexedName(obj) {
  if (!obj) return "None";
  return `${obj.index} ${obj.name}`;
}

function FormFields({ isOriginal, isTranslate, form }: FormFieldsProps) {
  const [instructions, setInstructions] = useState("");
  const locked = isOriginal || isTranslate;
  const editorRef = useRef<{ setContent(content: string): void }>(null);

  const course = useCourse();
  const section = useSection();
  const lesson = section?.parent;
  const unit = lesson?.parent;
  const prev = section?.previous;
  const next = section?.next;
  const [isGenerating, setIsGenerating] = useState(false);

  const handleChange = (e: any) => {
    const inputValue = e.target.value;
    if (inputValue.length <= MAX_INPUT_LENGTH) {
      setInstructions(inputValue);
    }
  };

  async function generateMdx() {
    setIsGenerating(true);
    const context = `
Course: ${course?.name}

Course Description: ${course?.description ? course?.description : "None"}

Unit: ${getIndexedName(unit)}

Lesson: ${getIndexedName(lesson)}

Current Section: ${getIndexedName(section)}

Previous Section: ${getIndexedName(prev)}

Next Section: ${getIndexedName(next)}
`;
    const response = await fetchGenerateMdx(context, instructions);
    editorRef.current?.setContent(response.code);
    setIsGenerating(false);
  }

  return (
    <div className="grid gap-4 py-4">
      <div className="flex gap-4">
        <Field label="Course Name" content={course?.name} />
        <Field label="Unit" content={getIndexedName(unit)} />
        <Field label="Lesson" content={getIndexedName(lesson)} />
        <Field label="Section" content={getIndexedName(section)} />
        {prev && (
          <Field label="Previous Section" content={getIndexedName(prev)} />
        )}
        {next && <Field label="Next Section" content={getIndexedName(next)} />}
      </div>
      <Field
        label="Course Description"
        content={`${course?.description ? course?.description : "None"}`}
      />

      <div className="flex flex-col">
        <div className="flex text-sm">Instructions (optional)</div>
        <Textarea
          placeholder="Type your instructions here..."
          value={instructions}
          onChange={handleChange}
          maxLength={MAX_INPUT_LENGTH}
          className="resize-y min-h-[80px]"
        />
        <span className="text-xs text-muted-foreground">
          {instructions.length} / {MAX_INPUT_LENGTH}
        </span>
      </div>
      <div>
        <Button
          className="w-48"
          onClick={async (e) => {
            e.preventDefault();
            generateMdx();
          }}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <Spinner label="Generating..." />
          ) : (
            "Generate Markdown"
          )}
        </Button>
       
          <span className="ms-4 text-xs text-muted-foreground">
             {isGenerating ? "This may take a few moments..." : "Using gpt-4.1-mini model"}
          </span>
        
      </div>

      <MarkdownField
        ref={editorRef}
        form={form}
        name="code"
        label="Markdown"
        isTranslate={isTranslate}
        disabled={isOriginal}
        vertical={!!locked}
        disabled={isGenerating}
      />

      {!locked && <CommonAdminFields form={form}></CommonAdminFields>}
    </div>
  );
}

function Field({
  label,
  content,
  className,
}: {
  label: string;
  content?: string;
  className?: string;
}) {
  return (
    <div>
      <div className="flex text-sm">{label}</div>
      <CodeHike
        className="flex border-2 border-secondary p-2 rounded-md"
        code={content}
      />
    </div>
  );
}

export default { Name, Defaults, FormFields };
